import * as React from 'react';
import { AlertCircle, Command, RefreshCw, ShieldAlert, Wifi, WifiOff } from 'lucide-react';

import { Button } from '../primitives/button';
import { cn } from '../lib/utils';
import { useFocusTrap } from '../lib/use-focus-trap';
import type { ConsoleCommand, ConsoleLiveStatus } from './console-shell';

/**
 * Canonical operational console archetype contract (light variant).
 *
 * Every operational console / workbench view should compose
 * `useConsoleArchetype()` and render `<ConsoleArchetypeBar />` near the top
 * of its body. The pair bundles the 4 affordances that the bar owns:
 *
 *   1. Keyboard command palette  — Cmd/Ctrl+K opens, arrow + enter to run,
 *                                  Esc to close. Filterable, sorted, accessible.
 *                                  Owned entirely by the hook + bar.
 *   3. Live status               — `liveStatus.connected` + label rendered
 *                                  as a Wifi / WifiOff pill.
 *   4. Stale data indicator      — `staleSince` ISO timestamp; flips to a
 *                                  stale banner once `staleAfterMs` passes.
 *   5. Retry                     — `onRetry` handler — wired into stale +
 *                                  offline banner buttons.
 *
 * The remaining 2 affordances stay on the view's command list:
 *
 *   2. Role-aware disabled       — `commands[i].deniedReason`
 *      actions with reason
 *   6. Safe command confirmation — `commands[i].requiresConfirm = true`
 *
 * `scripts/audit-operational-console-archetype-coverage.mjs` classifies each
 * console from its TypeScript AST, so ADOPTING THE HOOK IS NOT ITSELF A SCORE:
 * the bar must be rendered, and each affordance is credited only when the view
 * supplies the option that makes it appear at runtime. In particular the bar
 * falls back to `{ connected: true, label: 'Live' }` when `liveStatus` is
 * omitted — a pill that reads "Live" whether or not anything is live — so an
 * omitted or literal `liveStatus` scores nothing, and neither does
 * `staleSince: null` or an empty `onRetry`. Pass values derived from the
 * queries the console actually reads.
 */

export interface UseConsoleArchetypeOpts {
  /** Stable view id; scopes the bar's `data-testid` namespace. */
  viewId: string;
  /** Visible command list (toolbar + palette). */
  commands?: ConsoleCommand[];
  /** Live status indicator. Default: `{ connected: true, label: 'Live' }`. */
  liveStatus?: ConsoleLiveStatus;
  /** ISO timestamp of last successful data refresh. */
  staleSince?: string | null;
  /** ms beyond which `staleSince` is considered stale. Default 30000. */
  staleAfterMs?: number;
  /** Retry handler invoked from the stale + offline banners. */
  onRetry?: () => void;
}

export interface UseConsoleArchetypeResult {
  /** Command list passed in (or empty array). */
  commands: ConsoleCommand[];
  /** Resolved live status (defaults applied). */
  liveStatus: ConsoleLiveStatus;
  /** Current stale-since marker. */
  staleSince: string | null;
  /** Whether the data is currently stale. */
  isStale: boolean;
  /** Retry handler (no-op if unset). */
  onRetry: () => void;
  /** Palette open state. */
  paletteOpen: boolean;
  /** Open the palette imperatively. */
  openPalette: () => void;
  /** Close the palette imperatively. */
  closePalette: () => void;
  /** Build a stable `data-testid` for an archetype affordance. */
  testId: (suffix: string) => string;
  /** View id (echoed). */
  viewId: string;
  /** ms beyond which staleSince is considered stale. */
  staleAfterMs: number;
}

export function useConsoleArchetype(
  opts: UseConsoleArchetypeOpts,
): UseConsoleArchetypeResult {
  const {
    viewId,
    commands = [],
    liveStatus,
    staleSince = null,
    staleAfterMs = 30_000,
    onRetry,
  } = opts;

  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      } else if (e.key === 'Escape') {
        setPaletteOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const resolvedLive: ConsoleLiveStatus = liveStatus ?? { connected: true, label: 'Live' };
  const isStale =
    staleSince != null && now - new Date(staleSince).getTime() > staleAfterMs;

  return {
    commands,
    liveStatus: resolvedLive,
    staleSince,
    isStale,
    onRetry: onRetry ?? (() => undefined),
    paletteOpen,
    openPalette: React.useCallback(() => setPaletteOpen(true), []),
    closePalette: React.useCallback(() => setPaletteOpen(false), []),
    testId: React.useCallback(
      (suffix: string) => `console-archetype-${viewId}-${suffix}`,
      [viewId],
    ),
    viewId,
    staleAfterMs,
  };
}

// ─── ConsoleArchetypeBar JSX ──────────────────────────────────────

export interface ConsoleArchetypeBarProps {
  archetype: UseConsoleArchetypeResult;
  /** Optional extra className for the outer container. */
  className?: string;
}

/**
 * Compact status bar to drop in at the top of any operational console.
 * Renders: live status pill, stale banner (when stale), retry button, and a
 * ⌘K trigger that opens the keyboard command palette. Visible toolbar is
 * built from the first 5 commands; the rest stay palette-only.
 */
export function ConsoleArchetypeBar({ archetype, className }: ConsoleArchetypeBarProps) {
  const {
    commands,
    liveStatus,
    staleSince,
    isStale,
    onRetry,
    paletteOpen,
    openPalette,
    closePalette,
    testId,
  } = archetype;

  const [paletteFilter, setPaletteFilter] = React.useState('');
  const [paletteIdx, setPaletteIdx] = React.useState(0);
  const [confirm, setConfirm] = React.useState<ConsoleCommand | null>(null);

  React.useEffect(() => {
    if (!paletteOpen) {
      setPaletteFilter('');
      setPaletteIdx(0);
    }
  }, [paletteOpen]);

  React.useEffect(() => {
    setPaletteIdx(0);
  }, [paletteFilter]);

  const filteredCommands = React.useMemo(() => {
    if (!paletteFilter) return commands;
    const q = paletteFilter.toLowerCase();
    return commands.filter(
      (c) => c.label.toLowerCase().includes(q) || c.hint?.toLowerCase().includes(q),
    );
  }, [commands, paletteFilter]);

  const handleCommand = React.useCallback(
    (cmd: ConsoleCommand) => {
      if (cmd.deniedReason) return;
      closePalette();
      if (cmd.requiresConfirm) {
        setConfirm(cmd);
      } else {
        void cmd.run();
      }
    },
    [closePalette],
  );

  const handlePaletteKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setPaletteIdx((i) => Math.min(filteredCommands.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setPaletteIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = filteredCommands[paletteIdx];
      if (cmd) handleCommand(cmd);
    }
  };

  const isOffline = liveStatus.connected === false;
  const showBanner = isStale || isOffline;

  const paletteTrapRef = useFocusTrap<HTMLDivElement>({
    active: paletteOpen,
    onEscape: closePalette,
  });
  const confirmTrapRef = useFocusTrap<HTMLDivElement>({
    active: confirm != null,
    onEscape: () => setConfirm(null),
  });

  return (
    <>
      <div
        className={cn(
          'flex flex-wrap items-center gap-2 rounded-md border border-border/60 bg-surface px-2 py-1.5 text-xs',
          className,
        )}
        role="toolbar"
        aria-label="Console controls"
        data-testid={testId('bar')}
      >
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 ring-1 ring-inset',
            liveStatus.connected
              ? 'bg-[hsl(var(--state-running))]/10 text-[hsl(var(--state-running))] ring-[hsl(var(--state-running))]/30'
              : 'bg-[hsl(var(--alarm-critical))]/10 text-[hsl(var(--alarm-critical))] ring-[hsl(var(--alarm-critical))]/40',
          )}
          data-testid={testId('live-status')}
        >
          {liveStatus.connected ? (
            <Wifi className="h-3 w-3" aria-hidden />
          ) : (
            <WifiOff className="h-3 w-3" aria-hidden />
          )}
          {liveStatus.label ?? (liveStatus.connected ? 'Live' : 'Disconnected')}
        </span>

        {staleSince ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-2 py-0.5 ring-1 ring-inset',
              isStale
                ? 'bg-[hsl(var(--alarm-minor))]/10 text-[hsl(var(--alarm-minor))] ring-[hsl(var(--alarm-minor))]/30'
                : 'bg-muted text-muted-foreground ring-border',
            )}
            data-testid={testId('stale-pill')}
            title={`Last fresh ${new Date(staleSince).toLocaleString()}`}
          >
            {isStale ? 'Stale data' : 'Fresh'}
          </span>
        ) : null}

        <div className="flex-1" />

        {/* Visible toolbar: first 5 commands. */}
        {commands.slice(0, 5).map((c) => (
          <Button
            key={c.id}
            size="sm"
            variant={c.variant === 'destructive' ? 'destructive' : 'ghost'}
            onClick={() => handleCommand(c)}
            disabled={!!c.deniedReason}
            title={c.deniedReason ?? c.hint}
            data-testid={testId(`cmd-${c.id}`)}
          >
            {c.deniedReason ? <ShieldAlert className="mr-1 h-3 w-3" aria-hidden /> : null}
            {c.label}
          </Button>
        ))}

        {onRetry !== undefined ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={onRetry}
            data-testid={testId('retry')}
            title="Retry"
          >
            <RefreshCw className="mr-1 h-3 w-3" aria-hidden />
            Retry
          </Button>
        ) : null}

        <Button
          size="sm"
          variant="outline"
          onClick={openPalette}
          data-testid={testId('palette-trigger')}
          aria-label="Open command palette"
        >
          <Command className="mr-1 h-3 w-3" aria-hidden /> ⌘K
        </Button>
      </div>

      {showBanner ? (
        <div
          className={cn(
            'mt-2 flex items-center justify-between gap-3 rounded border px-3 py-2 text-sm',
            isOffline
              ? 'border-[hsl(var(--alarm-critical))]/40 bg-[hsl(var(--alarm-critical))]/10 text-[hsl(var(--alarm-critical))]'
              : 'border-[hsl(var(--alarm-minor))]/40 bg-[hsl(var(--alarm-minor))]/10 text-[hsl(var(--alarm-minor))]',
          )}
          role="status"
          aria-live="polite"
          data-testid={isOffline ? testId('offline-banner') : testId('stale-banner')}
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" aria-hidden />
            <span>
              {isOffline
                ? 'Connection to live source lost.'
                : `Data is stale (last update ${staleSince ? new Date(staleSince).toLocaleTimeString() : 'unknown'}).`}
            </span>
          </div>
          {onRetry ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={onRetry}
              data-testid={testId('banner-retry')}
            >
              <RefreshCw className="mr-1 h-4 w-4" aria-hidden /> Retry
            </Button>
          ) : null}
        </div>
      ) : null}

      {paletteOpen ? (
        <div
          role="presentation"
          className="fixed inset-0 z-40 flex items-start justify-center bg-black/30 p-12"
          data-testid="console-palette"
          onClick={(e) => {
            // Backdrop click only — clicks inside the dialog bubble up here too.
            if (e.target === e.currentTarget) closePalette();
          }}
        >
          <div
            ref={paletteTrapRef}
            role="dialog"
            aria-modal="true"
            aria-label="Command palette"
            className="w-full max-w-lg rounded-lg bg-background p-2 shadow-xl"
          >
            <input
              className="w-full rounded border border-border px-3 py-2 text-sm"
              placeholder="Type a command…"
              value={paletteFilter}
              onChange={(e) => setPaletteFilter(e.target.value)}
              onKeyDown={handlePaletteKey}
              data-testid="console-palette-input"
            />
            <ul className="mt-2 max-h-72 overflow-auto" role="listbox">
              {filteredCommands.length === 0 ? (
                <li className="px-3 py-2 text-sm text-muted-foreground">No matching commands</li>
              ) : (
                filteredCommands.map((c, i) => (
                  <li
                    key={c.id}
                    role="option"
                    aria-selected={i === paletteIdx}
                    onClick={() => handleCommand(c)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCommand(c);
                      }
                    }}
                    className={cn(
                      'flex cursor-pointer items-center justify-between rounded px-3 py-2 text-sm',
                      i === paletteIdx && 'bg-accent',
                      c.deniedReason && 'opacity-50',
                    )}
                    data-testid={`console-palette-item-${c.id}`}
                  >
                    <div>
                      <div className="font-medium">{c.label}</div>
                      {c.deniedReason ? (
                        <div className="text-xs text-[hsl(var(--alarm-critical))]">{c.deniedReason}</div>
                      ) : c.hint ? (
                        <div className="text-xs text-muted-foreground">{c.hint}</div>
                      ) : null}
                    </div>
                    {c.shortcut ? (
                      <kbd className="rounded border bg-muted px-1.5 py-0.5 text-xs">{c.shortcut}</kbd>
                    ) : null}
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      ) : null}

      {confirm ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="console-archetype-confirm-title"
          data-testid="console-confirm-modal"
        >
          <div
            ref={confirmTrapRef}
            className="w-full max-w-md rounded-lg bg-background p-4 shadow-lg"
          >
            <h2 id="console-archetype-confirm-title" className="text-lg font-semibold">
              Confirm: {confirm.label}
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {confirm.confirmPrompt ?? 'This action cannot be undone. Continue?'}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant={confirm.variant === 'destructive' ? 'destructive' : 'default'}
                size="sm"
                onClick={() => {
                  const c = confirm;
                  setConfirm(null);
                  void c.run();
                }}
                data-testid="console-confirm-run"
              >
                Run
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
